/* eslint-disable react-hooks/rules-of-hooks */
import { useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Button,
  Collapse,
  ListItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block',
    paddingTop: 2,
    paddingBottom: 2,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 2,
    paddingBottom: 2,
  },
  button: {
    color: 'rgba(255,255,255,.72)',
    padding: '9px 10px',
    borderRadius: 9,
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontSize: 13.5,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,.10)',
      color: theme.palette.common.white,
    },
  },
  buttonLeaf: {
    color: 'rgba(255,255,255,.72)',
    padding: '9px 10px',
    borderRadius: 9,
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontSize: 13.5,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,.10)',
      color: theme.palette.common.white,
    },
  },
  buttonCollapsed: {
    justifyContent: 'center',
    minWidth: 0,
    padding: 9,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1.25),
    '& svg': {
      fontSize: 20,
    },
  },
  iconCollapsed: {
    marginRight: 0,
  },
  title: {
    marginRight: 'auto',
    whiteSpace: 'nowrap',
  },
  // White label: el sidebar ya lleva el primary del tenant de fondo; el ítem
  // activo usa overlay blanco para contrastar sobre cualquier color de marca.
  active: {
    backgroundColor: 'rgba(255,255,255,.16)',
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,.22)',
    },
    '& $icon': {
      color: theme.palette.common.white,
    },
  },
}));

function NavItem({
  title,
  href,
  depth,
  children,
  icon: Icon,
  className,
  open: openProp,
  info: Info,
  collapsed,
  onNeedExpand,
  ...rest
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    // En modo colapsado, expandir el sidebar antes de abrir el grupo
    if (collapsed && onNeedExpand) {
      onNeedExpand();
      setOpen(true);
      return;
    }
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 10;

  if (depth > 0) {
    paddingLeft = 18 + 8 * depth;
  }

  const style = collapsed ? undefined : {
    paddingLeft,
  };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Tooltip
          title={collapsed ? title : ''}
          placement="right"
        >
          <Button
            className={clsx(
              classes.button,
              collapsed && classes.buttonCollapsed,
            )}
            onClick={handleToggle}
            style={style}
          >
            {Icon && (
              <Icon className={clsx(
                classes.icon,
                collapsed && classes.iconCollapsed,
              )} size="20" />
            )}
            {!collapsed && (<>
              <span className={classes.title}>{title}</span>
              {open ? (
                <ExpandLessIcon size="small" color="inherit" />
              ) : (
                <ExpandMoreIcon size="small" color="inherit" />
              )}
            </>)}
          </Button>
        </Tooltip>
        {!collapsed && <Collapse in={open}>{children}</Collapse>}
      </ListItem>
    );
  }
  const location = useLocation();
  const loc = location.pathname + location.search;

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
      {...rest}
    >
      <Tooltip
        title={collapsed ? title : ''}
        placement="right"
      >
        <Button
          activeClassName={loc === href ? classes.active : ''}
          className={clsx(
            classes.buttonLeaf,
            `depth-${depth}`,
            collapsed && classes.buttonCollapsed,
          )}
          component={RouterLink}
          exact
          style={style}
          to={href}
        >
          {Icon && (
            <Icon className={clsx(
              classes.icon,
              collapsed && classes.iconCollapsed,
            )} size="20" />
          )}
          {!collapsed && <span className={classes.title}>{title}</span>}
          {!collapsed && Info && <Info className={classes.info} />}
        </Button>
      </Tooltip>
    </ListItem>
  );
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  info: PropTypes.any,
  onNeedExpand: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
  open: false,
  collapsed: false,
  onNeedExpand: null,
};

export default NavItem;
